import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  clearReconnectParam,
  isReconnectOpen,
  RECONNECT_ACCOUNTS_FLAG,
  replaceSharedUrl,
  useEmitReconnectSucceeded,
  useFeatureFlags,
} from '@robinpowered/dashboard-apps-common';
import { useCalendarIntegrationHealth } from 'hooks/useCalendarIntegrationHealth';
import { ExternalAccountType } from 'generated/graphql';
import { useAmplitude } from 'contexts/AmplitudeContext';
import { AmplitudeEvents } from 'constants/amplitudeEvents';
import { Sentry } from 'lib/sentry';
import { useOAuthReconnect } from './useOAuthReconnect';
import { ReconnectAccountsModal } from './ReconnectAccountsModal';

// Which Amplitude event each OAuth outcome reports. Cancelling is tracked as
// deliberately as succeeding: a high cancel rate is the signal that the
// consent screen is asking for something users aren't willing to grant.
const OUTCOME_EVENT = {
  succeeded: AmplitudeEvents.RECONNECT_ACCOUNTS_SUCCEEDED,
  cancelled: AmplitudeEvents.RECONNECT_ACCOUNTS_CANCELLED,
  failed: AmplitudeEvents.RECONNECT_ACCOUNTS_FAILED,
} as const;

/**
 * Shell-hosted "Reconnect My Accounts" modal. Any micro-frontend can open it
 * from anywhere by setting the `reconnect` query param (see
 * `openReconnectAccounts` in `@robinpowered/dashboard-apps-common`) — this
 * component watches that param, probes calendar health while open, and
 * drives the in-place OAuth reconnect flow for each row.
 *
 * Mounted once, always, alongside the other shell-level overlay hosts
 * (`EventOverlayHost`, ...). It owns every effect this feature needs; the
 * modal it renders is purely presentational.
 */
export const ReconnectAccountsHost = (): JSX.Element | null => {
  const { checkFlag, loading } = useFeatureFlags();

  // Gated at the root, following `EventOverlayHost`. Two reasons this matters
  // beyond a normal rollout switch. The in-place OAuth needs client IDs that
  // no environment configures yet, and while the flag is off the schedule
  // banner keeps its original link to the dashboard integrations page — so a
  // user with a broken calendar always has a working way to fix it. Gating
  // here rather than inside also means the health probe never runs when the
  // feature is off.
  if (!checkFlag(RECONNECT_ACCOUNTS_FLAG) || loading) {
    return null;
  }

  return <ReconnectAccountsHostEnabled />;
};

const ReconnectAccountsHostEnabled = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const isOpen = isReconnectOpen(searchParams);

  const { health, refetching, refetch } = useCalendarIntegrationHealth({
    enabled: isOpen,
  });
  const { reconnect } = useOAuthReconnect();
  const { trackEvent } = useAmplitude();
  const emitReconnectSucceeded = useEmitReconnectSucceeded();

  const [pendingType, setPendingType] = useState<ExternalAccountType | null>(
    null
  );
  const [failedTypes, setFailedTypes] = useState<Set<ExternalAccountType>>(
    () => new Set()
  );

  // A closed modal should never reopen showing a stale spinner or error from
  // a previous visit — mirrors the health hook's own reset on `enabled` for
  // the OAuth-attempt state we own locally here.
  useEffect(() => {
    if (!isOpen) {
      setPendingType(null);
      setFailedTypes(new Set());
    }
  }, [isOpen]);

  // Reported on open rather than on mount: the host is mounted for the whole
  // session, so mount says nothing about whether anyone saw the modal. This
  // is the denominator for how often opening it leads to a reconnect.
  useEffect(() => {
    if (isOpen) {
      trackEvent(AmplitudeEvents.RECONNECT_ACCOUNTS_VIEWED);
    }
  }, [isOpen, trackEvent]);

  // Drops the `reconnect` param while preserving every other param on the
  // URL. Goes through `replaceSharedUrl` (not `setSearchParams`) because
  // shell and each MFE mount their own `BrowserRouter`: closing is
  // transient state that shouldn't add a history entry, and every Router on
  // the page — not just shell's — needs to observe the close.
  const handleClose = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    clearReconnectParam(params);
    const query = params.toString();
    replaceSharedUrl(`${window.location.pathname}${query ? `?${query}` : ''}`);
  }, []);

  // Tracks whether this open session ever showed a broken connection, so we
  // only auto-close after the user has actually fixed things — not when the
  // modal is opened while everything is already healthy (that case still shows
  // the "all working" state). Resets every time the modal closes.
  const sawBrokenRef = useRef(false);

  // Once the connections the user came to fix are all healthy, close the modal
  // for them rather than leaving them on an empty success state — e.g. after
  // reconnecting the last broken provider. Guarded on `!uncertain` so we never
  // close while a provider's status couldn't be confirmed.
  useEffect(() => {
    if (!isOpen) {
      sawBrokenRef.current = false;
      return;
    }
    if (health.state === 'ready' && health.needsAttention.length > 0) {
      sawBrokenRef.current = true;
    }
    if (
      sawBrokenRef.current &&
      health.state === 'ready' &&
      !health.uncertain &&
      health.needsAttention.length === 0
    ) {
      handleClose();
    }
  }, [isOpen, health, handleClose]);

  // Runs the in-place OAuth handshake for one provider row and maps its
  // outcome onto this row's UI state. Called straight from the row's
  // onClick — `reconnect` opens a popup, and every major browser blocks
  // popups that aren't attributable to a user gesture.
  const handleReconnect = useCallback(
    (accountType: ExternalAccountType) => {
      setFailedTypes((prev) => {
        if (!prev.has(accountType)) return prev;
        const next = new Set(prev);
        next.delete(accountType);
        return next;
      });
      setPendingType(accountType);
      trackEvent(AmplitudeEvents.RECONNECT_ACCOUNTS_STARTED, { accountType });

      const promise = reconnect(accountType).then((outcome) => {
        setPendingType(null);
        trackEvent(OUTCOME_EVENT[outcome], { accountType });

        if (outcome === 'succeeded') {
          // Tell any MFE rendering its own account-health UI to refetch,
          // then refresh this modal's own list so the row disappears here
          // too, in place.
          emitReconnectSucceeded(accountType);
          void refetch();
          return;
        }

        if (outcome === 'failed') {
          setFailedTypes((prev) => new Set(prev).add(accountType));
        }
        // 'cancelled' is an ordinary change of mind, not a fault — stay
        // silent and leave the row exactly as it was.
      });
      // `reconnect` resolves every outcome itself, so this only catches a
      // genuine defect. Without it the row would keep spinning forever with
      // nothing reported anywhere.
      promise.catch((error: unknown) => {
        Sentry.captureException(error, {
          tags: { feature: 'reconnect-accounts' },
          extra: { accountType },
        });
        setPendingType(null);
        setFailedTypes((prev) => new Set(prev).add(accountType));
      });
    },
    [emitReconnectSucceeded, reconnect, refetch, trackEvent]
  );

  return (
    <ReconnectAccountsModal
      open={isOpen}
      onClose={handleClose}
      health={health}
      refetching={refetching}
      onRetry={() => void refetch()}
      pendingType={pendingType}
      failedTypes={failedTypes}
      onReconnect={handleReconnect}
    />
  );
};
